import React, { useEffect, useRef, useState } from 'react';
import { Play } from 'lucide-react';
import {
  STUDIO_COPY,
  STUDIO_LANDING_REEL_POSTER,
  STUDIO_LANDING_REEL_SRC,
} from '../config';

function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduce(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  return reduce;
}

function isPlayableSrc(src: string | null | undefined): src is string {
  return typeof src === 'string' && src.trim().length > 0;
}

type StudioLandingReelProps = {
  /** Override for tests — production reads STUDIO_LANDING_REEL_SRC. */
  src?: string | null;
};

function PosterStill() {
  return (
    <>
      <img
        className="studio-landing-reel-media"
        src={STUDIO_LANDING_REEL_POSTER}
        alt=""
        data-testid="studio-landing-reel-poster"
      />
      <div className="studio-landing-reel-play" aria-hidden="true">
        <span className="studio-landing-reel-play-btn">
          <Play size={28} strokeWidth={2.25} />
        </span>
      </div>
    </>
  );
}

/**
 * Hero reel. Autoplay muted + loop when a public src is set. Missing src or
 * a load failure always degrade to the poster still + play mark — never an
 * empty <video>. Never a signed GCS URL.
 */
const StudioLandingReel: React.FC<StudioLandingReelProps> = ({
  src = STUDIO_LANDING_REEL_SRC,
}) => {
  const reduceMotion = usePrefersReducedMotion();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [failed, setFailed] = useState(false);
  const canPlay = isPlayableSrc(src) && !failed;
  const autoplay = canPlay && !reduceMotion;

  useEffect(() => {
    setFailed(false);
  }, [src]);

  useEffect(() => {
    if (!autoplay || !videoRef.current) return;
    try {
      const play = videoRef.current.play();
      if (play && typeof play.catch === 'function') {
        play.catch(() => undefined);
      }
    } catch {
      // jsdom has no media playback
    }
  }, [autoplay]);

  return (
    <section className="studio-landing-reel" data-testid="studio-landing-reel">
      <div className="studio-landing-reel-stage">
        {canPlay ? (
          <video
            ref={videoRef}
            className="studio-landing-reel-media"
            poster={STUDIO_LANDING_REEL_POSTER}
            src={src}
            autoPlay={autoplay}
            muted
            loop={autoplay}
            controls={!autoplay}
            playsInline
            preload="metadata"
            onError={() => setFailed(true)}
            data-testid="studio-landing-reel-video"
          />
        ) : (
          <PosterStill />
        )}
      </div>
      <p className="studio-landing-reel-caption">
        {STUDIO_COPY.landingReelCaption}
      </p>
    </section>
  );
};

export default StudioLandingReel;
